import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordStrength, scorePassword } from "@/components/PasswordStrength";
import { toast } from "sonner";
import { KeyRound, Mail, ShieldCheck, History } from "lucide-react";
import { Link } from "react-router-dom";
import { ActiveSessions, TwoFactorToggle } from "@/components/SecurityExtras";

const field = "w-full bg-transparent border-b border-white/15 py-3 text-base focus:border-gold outline-none transition-colors placeholder:text-text-muted/50";
const btn = "px-6 py-3 rounded-full border border-gold/50 text-gold text-xs uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors disabled:opacity-40";

export function ChangePassword({ hasPassword }) {
  const [cur, setCur] = useState(""); const [pw, setPw] = useState(""); const [pw2, setPw2] = useState(""); const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (scorePassword(pw) < 2) return toast.error("Choose a stronger password (8+ characters, a letter and a number)");
    setBusy(true);
    try {
      await api.post("/account/change-password", { current_password: hasPassword ? cur : undefined, new_password: pw });
      toast.success(hasPassword ? "Password changed — other devices were signed out" : "Password set");
      setCur(""); setPw(""); setPw2(""); await refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form data-testid="change-password-form" onSubmit={submit} className="rounded-2xl border border-white/10 bg-surface p-6 space-y-4">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><KeyRound size={14} /> {hasPassword ? "Change password" : "Set a password"}</p>
      {!hasPassword && <p className="text-xs text-text-muted">You signed up with Google. Add a password to also sign in with email.</p>}
      {hasPassword && <input data-testid="current-password" type="password" className={field} placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} required autoComplete="current-password" />}
      <div>
        <input data-testid="new-password" type="password" className={field} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="new-password" />
        <PasswordStrength value={pw} />
      </div>
      <input data-testid="new-password-confirm" type="password" className={field} placeholder="Repeat new password" value={pw2} onChange={(e) => setPw2(e.target.value)} required autoComplete="new-password" />
      <button data-testid="change-password-submit" disabled={busy} className={btn}>{busy ? "Saving…" : hasPassword ? "Update password" : "Set password"}</button>
    </form>
  );
}

function ChangeEmail({ email, hasPassword }) {
  const [next, setNext] = useState(""); const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false); const [pending, setPending] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/account/change-email", { new_email: next.trim(), password: hasPassword ? pw : undefined });
      setPending(data.new_email); setNext(""); setPw("");
      toast.success(`Confirmation sent to ${data.new_email}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form data-testid="change-email-form" onSubmit={submit} className="rounded-2xl border border-white/10 bg-surface p-6 space-y-4">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><Mail size={14} /> Change email</p>
      <p className="text-xs text-text-muted">Currently <span data-testid="current-email" className="text-text-main">{email}</span>. We'll send a confirmation link to the new address; nothing changes until you tap it.</p>
      {pending && <p data-testid="email-change-pending" className="text-xs text-gold border border-gold/30 bg-gold/5 rounded-xl px-3 py-2">Waiting for confirmation at {pending}</p>}
      <input data-testid="new-email" type="email" className={field} placeholder="New email address" value={next} onChange={(e) => setNext(e.target.value)} required />
      {hasPassword && <input data-testid="email-password" type="password" className={field} placeholder="Your password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="current-password" />}
      <button data-testid="change-email-submit" disabled={busy} className={btn}>{busy ? "Sending…" : "Send confirmation"}</button>
    </form>
  );
}

export function AccountSecurity() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <section data-testid="account-security" className="mt-16">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-text-muted mb-5"><ShieldCheck size={14} className="text-gold" /> Account & security</p>
      <div className="grid grid-cols-[minmax(0,1fr)] md:grid-cols-2 gap-5 [&>*]:min-w-0">
        <ChangePassword hasPassword={!!user.has_password} />
        <ChangeEmail email={user.email} hasPassword={!!user.has_password} />
        <TwoFactorToggle />
        <ActiveSessions />
      </div>
      <Link to="/security/sign-ins" data-testid="open-signin-history" className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-gold hover:underline">
        <History size={14} /> View full sign-in history
      </Link>
    </section>
  );
}
